import type { ClientContractRelation, ContractListFacets, ContractListItem } from "@closer/shared";
import { Button, Card, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * Sözleşme OLUŞTU ama bağlanamadı — iki adımı ayırt eden hata.
 *
 * ⚠ Ayırt etmek şart: "sözleşme oluşturulamadı" demek, oluşmuş bir kaydı kullanıcıdan
 * gizlerdi ve o ikinci bir tane açardı.
 */
class BaglanamadiHatasi extends Error {
  constructor(readonly sebep: unknown) {
    super("Sözleşme oluşturuldu ama müvekkile bağlanamadı");
    this.name = "BaglanamadiHatasi";
  }
}

/**
 * MÜVEKKİL PORTFÖYÜ (CLOS-173 `[120]`).
 *
 * ### ⚠ "Taraf" DEMİYOR
 *
 * Kartın bağımlılık sınırı: *"müvekkili sözleşmeye bağlamak sözleşme TARAFLIĞI, kayıt
 * erişimi, temsil, hizmet veya faturalama sonucu doğurmaz. Bu ayrım API, veri modeli ve UI
 * METİNLERİNDE korunur."* Bu yüzden ekranda "taraf" kelimesi geçmiyor: bölüm "Portföydeki
 * sözleşmeler", eylem "portföye ekle". Kullanıcı iki kavramı karıştırırsa, tarafı olmadığı
 * bir sözleşmede müvekkilini taraf sanır.
 *
 * ### ⚠ Görünen liste bir YETKİ DEĞİL
 *
 * Sunucu portföyü kullanıcının erişebildiği sözleşmelerle süzüyor; erişilemeyen bir bağ
 * hiç dönmüyor. Bu yüzden sayı "portföydeki tüm sözleşmeler" değil "senin görebildiklerin"
 * — ekran da bunu böyle söylüyor.
 */

export function ClientPortfolio({ clientId, canManage }: { clientId: string; canManage: boolean }) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [arama, setArama] = useState("");
  const [yeniBaslik, setYeniBaslik] = useState("");

  const portfoy = useQuery({
    queryKey: tenantKey(tenantId, "client-portfolio", clientId),
    queryFn: async () =>
      (await api<ClientContractRelation[]>(`/api/clients/${clientId}/contracts`, { tenantId }))
        .data,
  });

  /** Aday sözleşmeler LİSTE ucundan: erişim süzgeci orada zaten uygulanıyor (`[084]`). */
  const adaylar = useQuery({
    enabled: canManage && arama.trim().length > 0,
    queryKey: tenantKey(tenantId, "client-portfolio-adaylar", arama),
    queryFn: async () =>
      (
        await api<{ items: ContractListItem[]; facets: ContractListFacets }>(
          `/api/contracts?limit=5&q=${encodeURIComponent(arama.trim())}`,
          { tenantId },
        )
      ).data.items,
  });

  async function tazele() {
    await queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "client-portfolio", clientId),
    });
  }

  /**
   * BU MÜVEKKİL İÇİN YENİ SÖZLEŞME (CLOS-824).
   *
   * ⚠ İKİ ADIM ama TEK niyet: sözleşme oluşturuluyor, ardından AYNI akışta bağlanıyor.
   * Kullanıcı önceden sözleşmeyi başka bir yerde açıp buraya dönüp aramak zorundaydı.
   *
   * ⚠ Bağlama adımı düşerse sözleşme SİLİNMİYOR ve kullanıcıya söyleniyor. Sessizce
   * yutsaydık kullanıcı sözleşmenin hiç oluşmadığını sanır ve ikinci bir tane açardı;
   * geri almaya kalksaydık, oluşmuş ve belki de içeriği girilmiş bir kaydı silerdik.
   */
  const olusturVeBagla = useMutation({
    mutationFn: async (baslik: string) => {
      const { data } = await api<{ id: string }>("/api/contracts", {
        method: "POST",
        tenantId,
        body: JSON.stringify({ title: baslik }),
      });
      try {
        await api(`/api/clients/${clientId}/contracts`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ contractId: data.id }),
        });
      } catch (hata) {
        throw new BaglanamadiHatasi(hata);
      }
      return data;
    },
    onSuccess: async () => {
      setYeniBaslik("");
      await tazele();
      toast.success("Sözleşme oluşturuldu ve portföye eklendi");
    },
    onError: (hata: unknown) => {
      if (hata instanceof BaglanamadiHatasi) {
        toast.error(
          "Sözleşme oluşturuldu ama müvekkile bağlanamadı; sözleşmeler listesinden bağlayabilirsiniz",
        );
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Sözleşme oluşturulamadı");
    },
  });

  const bagla = useMutation({
    mutationFn: (contractId: string) =>
      api<ClientContractRelation>(`/api/clients/${clientId}/contracts`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ contractId }),
      }),
    onSuccess: async () => {
      setArama("");
      await tazele();
      toast.success("Sözleşme portföye eklendi");
    },
    onError: (hata) => {
      toast.error(hata instanceof ApiRequestError ? hata.message : "Sözleşme eklenemedi");
    },
  });

  const kaldir = useMutation({
    mutationFn: (contractId: string) =>
      api<ClientContractRelation>(`/api/clients/${clientId}/contracts/${contractId}`, {
        method: "DELETE",
        tenantId,
      }),
    onSuccess: async () => {
      await tazele();
      toast.success("Sözleşme portföyden çıkarıldı");
    },
    onError: (hata) => {
      toast.error(hata instanceof ApiRequestError ? hata.message : "Sözleşme çıkarılamadı");
    },
  });

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-fg-heading">Portföydeki sözleşmeler</h2>

        {canManage && (
          <div className="flex flex-col gap-2">
            {/*
              ⚠ ÖNCE OLUŞTURMA, sonra arama (CLOS-824). Kullanıcının bu sayfadaki niyeti
              çoğunlukla "bu müvekkile bir sözleşme yapacağım"; var olanı aramak ikincil.
            */}
            <div className="flex gap-2">
              <Input
                aria-label="Yeni sözleşme başlığı"
                placeholder="Yeni sözleşme başlığı"
                value={yeniBaslik}
                onChange={(e) => setYeniBaslik(e.target.value)}
              />
              <Button
                type="button"
                variant="secondary"
                loading={olusturVeBagla.isPending}
                disabled={yeniBaslik.trim().length === 0}
                onClick={() => olusturVeBagla.mutate(yeniBaslik.trim())}
              >
                Oluştur ve bağla
              </Button>
            </div>

            <Input
              aria-label="Portföye eklenecek sözleşmeyi ara"
              placeholder="Sözleşme başlığı"
              value={arama}
              onChange={(e) => setArama(e.target.value)}
            />
            {(adaylar.data ?? []).length > 0 && (
              <ul className="flex flex-col gap-1">
                {(adaylar.data ?? []).map((aday) => (
                  <li key={aday.id}>
                    <Button
                      variant="secondary"
                      onClick={() => bagla.mutate(aday.id)}
                      className="w-full !justify-start !text-left"
                    >
                      {aday.title}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {portfoy.isPending ? (
          <div role="status" aria-label="Portföy yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : portfoy.error ? (
          <StateView
            state={resolveErrorState(portfoy.error, { idempotent: true })}
            onRetry={() => void portfoy.refetch()}
          />
        ) : (portfoy.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">
            Bu müvekkilin portföyünde görebildiğiniz bir sözleşme yok.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(portfoy.data ?? []).map((iliski) => (
              <li key={iliski.id} className="flex items-center gap-2 text-sm">
                {/*
                  ⚠ Bağlantı bir yetki değil: sözleşme detayı kendi kayıt erişimini yeniden
                  doğruluyor. Portföyde görünmek, açılabilir olmak anlamına gelmiyor.
                */}
                <Link
                  className="font-medium text-primary hover:underline"
                  to={`/app/sozlesmeler/${iliski.contractId}`}
                >
                  {iliski.contractTitle ?? iliski.contractId}
                </Link>
                {canManage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => kaldir.mutate(iliski.contractId)}
                  >
                    Portföyden çıkar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
